import { unusablePattern } from './rules';
/**
 * Splitting a long text into parts an app can fetch one at a time.
 *
 * A generated app is one inlined HTML document. A Bible is roughly 4 MB and a Quran with
 * translation around 1 MB; neither can be inlined, and neither should be regenerated by a
 * model — a scripture reader that paraphrases its source is worse than useless. So the
 * text is stored once and read a chapter at a time.
 *
 * Pure functions: the caller does the storing. Splitting is deliberately dumb and
 * predictable — a heading pattern the uploader chooses — because a clever splitter that
 * guesses wrong reorders somebody's book.
 */
export interface TextPart {
  /** Stable, URL-safe, and derived from the label so a link to chapter 3 keeps working. */
  id: string;
  label: string;
  text: string;
}

export interface TextManifest {
  name: string;
  title: string;
  /** Named on screen wherever the text is shown — a translation is somebody's work. */
  attribution: string;
  parts: { id: string; label: string; chars: number }[];
}

/** Cap per part: big enough for a long chapter, small enough to be one quick fetch. */
const MAX_PART_CHARS = 200_000;

export function partId(label: string, taken: Set<string>): string {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'part';
  let id = base;
  let n = 2;
  while (taken.has(id)) id = `${base}-${n++}`;
  taken.add(id);
  return id;
}

/**
 * Split on a heading pattern, keeping each heading with the text beneath it.
 *
 * Anything before the first heading is kept as a part of its own rather than dropped — a
 * preface is still the author's, and silently losing it would be the worst kind of bug in
 * something whose whole job is to reproduce a text faithfully.
 */
export function splitText(raw: string, headingPattern?: string): TextPart[] {
  const text = raw.replace(/\r\n/g, '\n').trim();
  if (!text) return [];

  let re: RegExp | null = null;
  if (headingPattern) {
    // The same bound as the schema's pattern rule, and for the same reason: this one runs
    // against a whole document, so a backtracking pattern here is the more expensive of the
    // two. See `unusablePattern`.
    const unusable = unusablePattern(headingPattern);
    if (unusable) {
      console.error('[longText] heading pattern not applied', { reason: unusable, pattern: headingPattern.slice(0, 120) });
    } else {
      try {
        re = new RegExp(`^(${headingPattern})\\s*$`, 'gm');
      } catch {
        re = null; // an unusable pattern falls back to length-splitting, never to failure
      }
    }
  }

  const taken = new Set<string>();
  if (!re) return chunk(text, taken);

  const marks: { index: number; label: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    marks.push({ index: m.index, label: m[1].trim() });
    if (m.index === re.lastIndex) re.lastIndex++; // a zero-width match must not spin
  }
  if (marks.length === 0) return chunk(text, taken);

  const out: TextPart[] = [];
  if (marks[0].index > 0) {
    const preface = text.slice(0, marks[0].index).trim();
    if (preface) out.push({ id: partId('preface', taken), label: 'Preface', text: preface });
  }
  marks.forEach((mark, i) => {
    const end = i + 1 < marks.length ? marks[i + 1].index : text.length;
    const body = text.slice(mark.index, end).trim();
    if (body) out.push({ id: partId(mark.label, taken), label: mark.label, text: body });
  });
  return out;
}

/** No headings to go on: fixed-size pieces, split at a paragraph break where one is near,
 *  so a part never begins mid-sentence. */
function chunk(text: string, taken: Set<string>): TextPart[] {
  const out: TextPart[] = [];
  let start = 0;
  let n = 1;
  while (start < text.length) {
    let end = Math.min(start + MAX_PART_CHARS, text.length);
    if (end < text.length) {
      const brk = text.lastIndexOf('\n\n', end);
      if (brk > start + MAX_PART_CHARS / 2) end = brk;
    }
    const label = `Part ${n}`;
    out.push({ id: partId(label, taken), label, text: text.slice(start, end).trim() });
    start = end;
    n++;
  }
  return out;
}

export function manifestOf(
  name: string,
  title: string,
  attribution: string,
  parts: TextPart[],
): TextManifest {
  return {
    name,
    title,
    attribution,
    parts: parts.map((p) => ({ id: p.id, label: p.label, chars: p.text.length })),
  };
}
